// Was der angemeldete Mensch sehen und dürfen darf.
//
// Zwei Dinge, die man nicht verwechseln darf:
//
//   MODULE  — was im Menü auftaucht. Reine Bequemlichkeit. Wer die Adresse
//             kennt, käme trotzdem hin; das ist in Ordnung, weil die Daten
//             dahinter ohnehin durch RLS geschützt sind.
//   RECHTE  — was jemand TUN darf. Der Knopf hier auszublenden ist nur Höflichkeit;
//             durchgesetzt wird es in der Datenbank (siehe Migration 029,
//             hat_recht()). Ein Frontend-Check ist keine Sicherheitsgrenze.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";

/** Alles, was per Haken vergeben werden kann. */
export const RECHTE = [
  { id: "produkte.anlegen", label: "Produkte anlegen und ändern" },
] as const;

export type RechtId = (typeof RECHTE)[number]["id"];

export interface MeineRechte {
  istAdmin: boolean;
  /** NULL = Standard nach Rolle. Sonst: genau diese Module. */
  allowedModules: string[] | null;
  rechte: string[];
}

export function useMeineRechte() {
  const id = useAuth((s) => s.session?.user.id);
  const rolle = useAuth((s) => s.role);

  return useQuery({
    queryKey: ["meine-rechte", id],
    enabled: !!id,
    // Rechte ändern sich selten, werden aber auf jeder Seite gebraucht.
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<MeineRechte> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("allowed_modules, permissions")
        .eq("id", id!)
        .single();
      if (error) throw error;

      return {
        istAdmin: rolle === "admin",
        allowedModules: (data?.allowed_modules as string[] | null) ?? null,
        rechte: (data?.permissions as string[] | null) ?? [],
      };
    },
  });
}

/** Kurzform für den Alltag: darf ich das? */
export function useDarfIch(recht: RechtId) {
  const { data } = useMeineRechte();
  // Ein Admin darf alles — sonst müsste man ihm die Haken selbst setzen.
  return !!data && (data.istAdmin || data.rechte.includes(recht));
}
